import { useState } from "react";
import { Link } from "react-router-dom";
import { 
  CloudLightning,
  CheckCircle,
  Clock,
  Shield,
  AlertTriangle,
  ArrowRight,
  Phone,
  FileText,
  Droplets,
  Wind,
  Snowflake,
  Camera,
  ClipboardList,
  Users,
  ChevronDown,
  ChevronUp,
  Home,
  Calendar
} from "lucide-react";
import { Button } from "@/components/ui/button";
import LeadForm from "@/components/LeadForm";
import useSEO from "@/hooks/useSEO";

const FAQ_DATA = [
  {
    question: "How quickly should I address storm damage?",
    answer: "Act within 24-48 hours if possible. Document damage immediately, contact your insurance company promptly, and schedule a professional inspection. Most insurance policies have time limits for filing claims, and delays can lead to secondary damage like water intrusion."
  },
  {
    question: "Does homeowners insurance cover storm damage?",
    answer: "Most homeowners insurance policies cover storm damage from hail, wind, and fallen trees. Coverage depends on your specific policy and the cause of damage. Age-related wear is typically not covered. Contact your insurance company promptly after discovering damage."
  },
  {
    question: "What should I do immediately after a storm?",
    answer: "First, ensure everyone is safe. Document visible damage with photos/video from the ground (don't climb on your roof). Cover any interior leaks to prevent water damage. Contact your insurance company to report potential damage, then schedule a professional inspection."
  },
  {
    question: "How can I tell if my roof has hail damage?",
    answer: "Look for dented gutters, damaged siding, or marks on outdoor AC units. On the roof, hail creates random dent patterns, granule loss, and bruised shingles. Many signs require professional inspection as damage isn't always visible from the ground."
  },
  {
    question: "What's the difference between hail and wind damage?",
    answer: "Hail damage typically shows as random dents, bruises, or granule loss across the roof surface. Wind damage usually appears as lifted, creased, or missing shingles, often concentrated on edges and ridges. Both can occur in the same storm."
  },
  {
    question: "Should I get multiple estimates for storm damage repair?",
    answer: "Getting 2-3 estimates is wise for significant repairs. However, be cautious of storm chasers offering unusually low prices. Focus on contractor reputation, licensing, insurance, and local references rather than just price."
  },
  {
    question: "How long does the insurance claim process take?",
    answer: "Simple claims may resolve in 2-4 weeks. Complex claims with significant damage can take 1-3 months. Factors include adjuster availability, documentation quality, contractor scheduling, and whether supplemental claims are needed."
  },
  {
    question: "What if I disagree with my insurance adjuster's assessment?",
    answer: "You can request a re-inspection, hire a public adjuster, or have your contractor meet with the adjuster to review findings. Document everything and communicate in writing. Most disputes can be resolved through proper documentation and professional advocacy."
  }
];

const DAMAGE_TYPES = [
  {
    icon: CloudLightning,
    title: "Hail Damage",
    description: "Hail can cause dents, bruises, granule loss, and cracked shingles. Even small hail can damage roofing materials, and large hail can cause immediate leaks.",
    signs: ["Dented gutters and downspouts", "Granules in gutters", "Random dent patterns on shingles", "Damaged flashing and vents"],
    urgency: "High"
  },
  {
    icon: Wind,
    title: "Wind Damage", 
    description: "High winds lift, crease, and tear shingles. Damage often concentrates on edges, ridges, and areas where wind creates uplift pressure.",
    signs: ["Missing shingles", "Lifted or curled edges", "Exposed underlayment", "Debris on roof surface"],
    urgency: "High"
  },
  {
    icon: Droplets,
    title: "Water/Leak Damage",
    description: "Water intrusion from compromised roofing can cause interior damage, mold growth, and structural issues if not addressed promptly.",
    signs: ["Ceiling water stains", "Dripping during rain", "Attic moisture or mold", "Peeling paint on walls"],
    urgency: "Critical"
  },
  {
    icon: Snowflake,
    title: "Ice Dam Damage",
    description: "Minnesota winters create ice dams that force water under shingles. Damage may not be visible until spring thaw reveals interior water damage.",
    signs: ["Icicles on gutters", "Ice buildup at roof edges", "Interior ceiling stains", "Peeling exterior paint"],
    urgency: "Moderate"
  }
];

const PROCESS_STEPS = [
  {
    step: "1",
    title: "Document & Report",
    description: "Photograph all visible damage from ground level. Contact your insurance company to report the damage and start the claim process.",
    icon: Camera,
    timeframe: "Within 24-48 hours"
  },
  {
    step: "2", 
    title: "Professional Inspection",
    description: "A qualified contractor inspects your roof, documents damage professionally, and provides a detailed assessment and repair estimate.",
    icon: ClipboardList,
    timeframe: "1-3 days"
  },
  {
    step: "3",
    title: "Insurance Coordination",
    description: "Your contractor can meet with insurance adjusters, provide documentation, and advocate for proper damage assessment and coverage.",
    icon: Shield,
    timeframe: "1-2 weeks"
  },
  {
    step: "4",
    title: "Restoration Work",
    description: "Once approved, repairs or replacement begins. Quality contractors protect your property, communicate progress, and ensure proper installation.",
    icon: Home,
    timeframe: "1-5 days typical"
  }
];

export default function StormDamageRestoration() {
  useSEO();
  const [openFaq, setOpenFaq] = useState(null);

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Storm Damage Restoration Guide for Minnesota Homeowners",
    "description": "Complete guide to storm damage restoration in Minnesota including hail damage, wind damage, insurance claims, and finding trusted contractors.",
    "author": {
      "@type": "Organization",
      "name": "StormCareMN"
    },
    "publisher": {
      "@type": "Organization", 
      "name": "StormCareMN",
      "url": "https://stormcaremn.com"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQ_DATA.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div data-testid="storm-damage-page" className="min-h-screen bg-white">
      {/* Schema Markup */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaMarkup) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      {/* Hero Section */}
      <section className="bg-navy py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-gray-400 text-sm mb-6">
                <Link to="/" className="hover:text-orange">Home</Link>
                <span>/</span>
                <span className="text-gray-300">Storm Damage Restoration</span>
              </nav>

              <div className="inline-flex items-center gap-2 bg-orange/20 text-orange text-sm font-medium px-4 py-2 rounded-full mb-6">
                <CloudLightning className="w-4 h-4" />
                Storm Recovery Resource
              </div>

              <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-white tracking-tight leading-tight mb-6">
                Storm Damage Restoration<br />
                <span className="text-orange">Guide for Minnesota Homeowners</span>
              </h1>

              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                Minnesota storms can strike fast and leave damage behind. This guide helps you understand 
                the restoration process, navigate insurance claims, and find trusted professionals to 
                restore your home.
              </p>

              <div className="flex flex-wrap gap-4">
                <Button asChild size="lg" className="bg-orange hover:bg-orange-600 text-white">
                  <a href="#get-help">
                    <Phone className="w-4 h-4 mr-2" />
                    Get Help Now
                  </a>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-gray-600 text-white hover:bg-white/10">
                  <Link to="/hail-map">
                    Check Recent Storms
                  </Link>
                </Button>
              </div>
            </div>

            <div className="hidden lg:block">
              <img 
                src="https://images.unsplash.com/photo-1527482797697-8795b05a13fe?w=600"
                alt="Storm damage assessment in Minnesota"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Urgency Banner */}
      <section className="bg-orange py-4">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-white text-center">
            <AlertTriangle className="w-5 h-5" />
            <p className="font-medium">
              <strong>Recent storm in your area?</strong> Document damage immediately and contact your insurance within 24-48 hours.
            </p>
            <Link to="/hail-map" className="underline hover:no-underline whitespace-nowrap">
              Check hail map →
            </Link>
          </div>
        </div>
      </section>

      {/* Damage Types Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-semibold text-navy mb-4">
              Types of Storm Damage in Minnesota
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Understanding what type of damage you're dealing with helps you communicate effectively 
              with insurance companies and contractors.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {DAMAGE_TYPES.map((type, idx) => (
              <div key={`damage-${idx}`} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-navy rounded-lg flex items-center justify-center flex-shrink-0">
                    <type.icon className="w-6 h-6 text-orange" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-heading text-xl font-semibold text-navy">{type.title}</h3>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        type.urgency === 'Critical' ? 'bg-red-100 text-red-700' :
                        type.urgency === 'High' ? 'bg-orange-100 text-orange-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {type.urgency} Priority
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm">{type.description}</p>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <h4 className="text-sm font-medium text-navy mb-2">Warning Signs:</h4>
                  <ul className="grid grid-cols-2 gap-2">
                    {type.signs.map((sign, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-orange mt-0.5 flex-shrink-0" />
                        {sign}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-semibold text-navy mb-4">
              The Storm Damage Restoration Process
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From discovery to restoration, understanding each step helps you navigate the process 
              with confidence.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROCESS_STEPS.map((step, idx) => (
              <div key={`step-${idx}`} className="bg-white rounded-xl p-6 border border-gray-200 relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-orange rounded-full flex items-center justify-center text-white font-bold">
                    {step.step}
                  </div>
                  <step.icon className="w-6 h-6 text-navy" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-navy mb-2">{step.title}</h3>
                <p className="text-gray-600 text-sm mb-4">{step.description}</p>
                <div className="flex items-center gap-2 text-orange text-sm">
                  <Clock className="w-4 h-4" />
                  {step.timeframe}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Insurance Tips */}
      <section className="py-16 bg-navy">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl font-semibold text-white mb-6">
                Navigating Insurance Claims
              </h2>
              <p className="text-gray-300 mb-8">
                The insurance claim process can feel overwhelming, but understanding your rights 
                and the process helps ensure fair treatment.
              </p>

              <div className="space-y-4">
                {[
                  { title: "Document Everything", desc: "Photos, videos, and written notes create a record that supports your claim." },
                  { title: "Report Promptly", desc: "Most policies have time limits. Report damage within 24-48 hours when possible." },
                  { title: "Get Professional Help", desc: "Contractors experienced with insurance claims can advocate on your behalf." },
                  { title: "Know Your Policy", desc: "Review your coverage, deductibles, and any depreciation factors before claims." },
                  { title: "Don't Accept First Offer", desc: "Initial assessments may miss damage. Supplemental claims address additional findings." }
                ].map((tip, idx) => (
                  <div key={`tip-${idx}`} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-orange mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-white">{tip.title}</h4>
                      <p className="text-gray-400 text-sm">{tip.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white/10 rounded-2xl p-8">
              <h3 className="font-heading text-xl font-semibold text-white mb-6">
                What to Have Ready for Your Claim
              </h3>
              <ul className="space-y-3">
                {[
                  "Policy number and insurance contact information",
                  "Date and time of storm (or approximate)",
                  "Photos/videos of all visible damage",
                  "List of damaged items with descriptions",
                  "Any temporary repairs made (with receipts)",
                  "Contact information for your contractor"
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-gray-300">
                    <FileText className="w-5 h-5 text-orange mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-semibold text-navy mb-4">
              Storm Damage FAQs
            </h2>
            <p className="text-gray-600">
              Common questions about storm damage restoration and insurance claims.
            </p>
          </div>

          <div className="space-y-4">
            {FAQ_DATA.map((faq, idx) => (
              <div 
                key={`faq-${idx}`}
                className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-100 transition-colors"
                >
                  <span className="font-heading font-semibold text-navy pr-4">{faq.question}</span>
                  {openFaq === idx ? (
                    <ChevronUp className="w-5 h-5 text-orange flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="get-help" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl font-semibold text-navy mb-4">
                Need Storm Damage Help?
              </h2>
              <p className="text-gray-600 mb-6">
                Don't navigate storm damage alone. Connect with trusted Minnesota professionals 
                who can assess your damage, help with insurance claims, and restore your home properly.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-orange" />
                  <span className="text-gray-700">Free professional damage assessment</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-orange" />
                  <span className="text-gray-700">Insurance claim assistance</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-orange" />
                  <span className="text-gray-700">Vetted, licensed Minnesota contractors</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-orange" />
                  <span className="text-gray-700">No pressure, honest guidance</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <Link to="/hail-map" className="text-orange hover:text-orange-600 font-medium flex items-center gap-2">
                  Check recent hail activity <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/service-areas" className="text-navy hover:text-orange font-medium flex items-center gap-2">
                  Find your city <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg">
              <h3 className="font-heading text-xl font-semibold text-navy mb-2 text-center">
                Get Storm Damage Help
              </h3>
              <p className="text-gray-600 text-center mb-6">
                Tell us about your situation
              </p>
              <LeadForm compact={true} />
            </div>
          </div>
        </div>
      </section>

      {/* Related Links */}
      <section className="py-12 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h3 className="font-heading text-lg font-semibold text-navy mb-6">Related Resources</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/roof-replacement" className="bg-gray-50 px-4 py-2 rounded-lg border border-gray-200 hover:border-orange/30 text-gray-700 hover:text-orange transition-colors text-sm">
              Roof Replacement Guide
            </Link>
            <Link to="/blog/signs-your-roof-has-storm-damage" className="bg-gray-50 px-4 py-2 rounded-lg border border-gray-200 hover:border-orange/30 text-gray-700 hover:text-orange transition-colors text-sm">
              Signs of Storm Damage
            </Link>
            <Link to="/blog/storm-damage-insurance-claim-guide" className="bg-gray-50 px-4 py-2 rounded-lg border border-gray-200 hover:border-orange/30 text-gray-700 hover:text-orange transition-colors text-sm">
              Insurance Claim Guide
            </Link>
            <Link to="/hail-map" className="bg-gray-50 px-4 py-2 rounded-lg border border-gray-200 hover:border-orange/30 text-gray-700 hover:text-orange transition-colors text-sm">
              Minnesota Hail Map
            </Link>
            <Link to="/faqs" className="bg-gray-50 px-4 py-2 rounded-lg border border-gray-200 hover:border-orange/30 text-gray-700 hover:text-orange transition-colors text-sm">
              More FAQs
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
